import { Box, Grid, Skeleton, Typography } from "@mui/material";
import { VerityAssessment } from "@verity/api-client/types";
import { CoreBanner, CoreButton, CoreChip, CoreFileCard, CoreSkeletonWrapper, CoreTabs } from "@verity/components/core";
import { Dispatch, SetStateAction } from "react";
import apiClient from "../../../../helpers/api-client.js";
import i18n from "../../../../i18n.js";
import DownloadPDFReportButton from "../../PDFGenerator";
import { LiveSessionNote, QuizInfoSummary, SessionInfo } from "../../TestResultsWrapper.js";
import styles from "./ProctoringResultView.module.css";
import ProctoringVideoPlayer from "./ProctoringVideoPlayer.js";
import { TimestampLinks } from "./Timestamps/TimestampLinks.js";
import { VideoWrapper } from "./VideoControls/VideoControlLayout.js";
import AttemptSelector from "./components/AttemptSelector.js";
import FlagLayout from "./components/FlagLayout.js";
import ResultViewBackButton from "./components/ResultViewBackButton.js";
import SessionStatusChip from "./components/StatusChip.js";
import StudentSelectorLayout from "./components/StudentSelectorLayout.js";
import SummaryButton from "./components/SummaryButton.js";
import SummaryPanel from "./components/SummaryPanel.js";
import { getIdPhotosSrc } from "./hooks/proctoringResultViewHelper.js";
import useProctoringResultView, { UseProctoringResultViewReturn } from "./hooks/useProctoringResultView.js";
import useVerityAssessmentResult from "./hooks/useVerityAssessmentResult.js";
import DeleteConfirmationModal from "./modals/DeleteConfirmationModal.js";
import { roomscanDisplayStateAtom } from "./store.js";
import ConfigurationPanel from "./tabs/ConfigurationPanel.js";
import IdPhotosPanel from "./tabs/IdPhotosPanel.js";
import LiveNotesPanel from "./tabs/LiveNotesPanel.js";
import NotesPanel, { RoomScanNotePanel } from "./tabs/NotePanel.js";
import RoomScanPanel from "./tabs/RoomScanPanel.js";
import ScratchNote from "./tabs/ScratchNote.js";
import SignatureTabPanel from "./tabs/SignaturePanel.js";
import { ExemptedStudent } from "@verity/components";
import { QuizResultPanel } from "./QuizResultPanel.js";
import AssignmentPanel from "./tabs/AssignmentPanel.js";
import UnsavedChangesModal from "./modals/UnsavedChangesModal.js";
import { getAssignmentDraft, getAssignmentResponseId } from "./hooks/verityAssessmentResultHelper.js";

export type ProctorGroup = Awaited<ReturnType<typeof apiClient.liveClient.getLiveProctorGroup>>;
export type LiveSessionInfo = {
	id: string;
	name: string;
	isPopIn: boolean;
	proctorGroups: (Partial<ProctorGroup> & { id: number })[];
};

const VIDEO_JS_FRAME_OPTIONS = {
	controls: false,
	responsive: false,
	aspectRatio: "16:9",
	fluid: true,
	userActions: {
		click: false,
	},
	controlBar: {
		playToggle: false,
		volumePanel: false,
		progressControl: false,
		remainingTimeDisplay: false,
		pictureInPictureToggle: false,
		fullscreenToggle: false,
	},
	bigPlayButton: false,
};

const VIDEO_JS_OPTIONS = {
	controls: true,
	responsive: true,
	aspectRatio: "16:9",
	fluid: true,
	userActions: {
		click: false,
	},
	controlBar: {
		playToggle: false,
		volumePanel: false,
		progressControl: false,
		remainingTimeDisplay: false,
		pictureInPictureToggle: false,
		fullscreenToggle: false,
	},
	bigPlayButton: false,
};

export interface VideoPlaybackState {
	timer: number;
	lastTime: number;
	skip: boolean;
}

type PlayAction = {
	type: "play";
	speed: number;
};

type PauseAction = {
	type: "pause";
};

type SkipAction = {
	type: "skip";
	time: number;
};

export type VideoPlaybackAction = PlayAction | PauseAction | SkipAction;

type ProctoringResultLayoutProps = {
	viewProps: Omit<ProctoringResultViewProps, "showTimestamps"> & { showTimestamps: boolean };
	logic: UseProctoringResultViewReturn;
};

function LmsAssessmentLayout({ viewProps, logic }: ProctoringResultLayoutProps) {
	const { showTimestamps } = viewProps;
	const {
		componentsControl,
		summaryControl,
		proctoringState,
		session,
		eventsControl,
		isLoading,
		notesControl,
		webcamControl,
		roomScanControl,
		timeOffset,
		tabControl,
		fullScreenControl,
		screenShareControl,
		proctoredMediaContainer,
	} = logic;

	const { assessmentName, studentName } = proctoringState;
	const { userInfo, sessionNumber, totalSessions, selectedProctoringSession, sessionUuid, sessionStatus } = session;
	const { all: events, startDate } = eventsControl;

	const { roomScan: roomScanNotesControl, session: sessionNotesControl } = notesControl;

	const { inFullscreen } = fullScreenControl;
	const { mainTab, secondaryTab } = tabControl;

	return (
		<Grid container sx={{ background: "white", minHeight: "100%" }}>
			<Grid item container md={12} lg={7} gap={"14px"} flexDirection={"column"} paddingBottom={"14px"} height={"100%"}>
				<Grid
					item
					container
					display="flex"
					minHeight="75px"
					alignItems="center"
					justifyContent="space-between"
					paddingX="3%"
					paddingY="1%"
					sx={{ borderBottom: "0.5px solid #CACACA" }}
				>
					<ResultViewBackButton
						showBackButton={componentsControl.back.showBackButton}
						backLabel={assessmentName}
						onBackButtonClick={componentsControl.back.handleBackButton}
					/>
					<Grid item container xs={12} sm={"auto"} flexShrink={0}>
						<CoreSkeletonWrapper loading={isLoading} width={"120px"} height={"48px"}>
							<DownloadPDFReportButton
								userInfo={userInfo}
								sessionNum={sessionNumber.toString()}
								totalSessions={totalSessions}
								severeCount={eventsControl.severeCount ?? 0}
								majorCount={eventsControl.majorCount ?? 0}
								minorCount={eventsControl.minorCount ?? 0}
								sessionNotes={sessionNotesControl.notes}
								webcamVidSources={webcamControl.sources[0] ?? {}}
								webcamTimestampsArr={webcamControl.timestampsArr}
								duration={webcamControl.duration}
								timeOffset={timeOffset}
								allEvents={events}
								roomScanVidSource={roomScanControl.sources}
								roomScanNotes={roomScanNotesControl.notes}
								roomScanDuration={roomScanControl.duration}
								roomScanTimestampsArr={roomScanControl.timestampsArr}
								personPhotoAvailable={Boolean(selectedProctoringSession?.personPhotoAvailable)}
								idPhotoAvailable={Boolean(selectedProctoringSession?.idPhotoAvailable)}
								uuid={sessionUuid}
								signatureType={selectedProctoringSession?.signatureType}
							/>
						</CoreSkeletonWrapper>
					</Grid>
				</Grid>

				<Grid item container paddingX="3%">
					<FlagLayout
						type="circle"
						severeCount={eventsControl.severeCount}
						majorCount={eventsControl.majorCount}
						minorCount={eventsControl.minorCount}
					/>
				</Grid>
				<Grid item container paddingX="3%">
					<CoreSkeletonWrapper loading={isLoading} width={"100%"} height={"48px"}>
						<CoreTabs
							tabs={tabControl.mainTabList}
							value={mainTab}
							secondary
							height={"36px"}
							onChange={(_, newTab) => {
								tabControl.handleMainTabChange(newTab);
							}}
							tabsProps={{
								sx: {
									maxWidth: "100%",
								},
								ref: tabControl.mainTabsRef,
							}}
							scrollable
							scrollButtons={tabControl.mainTabOverflow}
						/>
					</CoreSkeletonWrapper>
				</Grid>
				<Grid
					item
					sx={{ height: "60vh", width: "100%", paddingX: "3%" }}
					id="media_grid"
					role="tabpanel"
					aria-labelledby={tabControl.mainTabIdRecord[mainTab]}
					key={mainTab}
				>
					{mainTab === "recording" && (
						<VideoWrapper inFullscreen={inFullscreen} showTimestamps={showTimestamps}>
							{logic.areVideosFetched ? (
								<ProctoringVideoPlayer
									isFullscreen={inFullscreen}
									sessionId={sessionUuid}
									showTimestamps={showTimestamps}
									controlOptions={webcamControl.options}
									playback={webcamControl.playback}
									playbackDispatch={webcamControl.dispatch}
									sessionRecordingTimestamps={eventsControl.sessionRecordingTimestamps}
									videos={{ webcam: webcamControl.sources, screencapture: webcamControl.screenSources }}
									startDate={startDate}
									duration={webcamControl.duration}
									timeOffset={timeOffset}
									setFullscreen={fullScreenControl.setFullscreen}
									setPlay={webcamControl.play}
									setVolume={(e) => webcamControl.setVolume(Number(e.target.value))}
									setSpeed={webcamControl.setSpeed}
									onEmailModalChange={componentsControl.modal.toggleEmailModal}
									shouldShowDownload={webcamControl.shouldShowDownload}
									showVideoUnavailable={webcamControl.showVideoUnavailable}
									showAudioThumbnail={webcamControl.showAudioThumbnail}
									webcamVideoOptions={VIDEO_JS_OPTIONS}
									screencaptureVideoOptions={VIDEO_JS_OPTIONS}
									shouldHideCamVid={webcamControl.shouldHideCamVid}
									events={events}
									sessionNotes={sessionNotesControl.notes}
									visibleLiveNotes={notesControl.visibleLiveNotes}
									toggleVolume={webcamControl.toggleVolume}
									isWebcamAndScreencaptureDeleted={selectedProctoringSession?.isWebcamAndScreenCaptureDeleted}
									webcamAndScreencaptureDeleteCause={selectedProctoringSession?.webcamAndScreenCaptureDeleteCause}
									setIsDeleteConfirmationModalShown={componentsControl.modal.toggleDeleteConfirmationModal}
									hasInstituteEditPermission={proctoringState.hasInstituteEditPermission}
									sessionStatus={sessionStatus}
									webCamHasError={webcamControl.hasError}
									setWebCamHasError={webcamControl.toggleError}
									selectedProctoringSessionUuid={sessionUuid}
									showMachineLearningOverlay={webcamControl.showMachineLearningOverlay}
									screenCaptureHasError={screenShareControl.hasError}
									setScreenCaptureHasError={screenShareControl.toggleError}
									proctoredMediaContainer={proctoredMediaContainer}
								/>
							) : (
								<Box width={"100%"} height={"100%"} display={"flex"} alignItems={"center"} justifyContent={"center"}>
									<Skeleton variant="rectangular" width={"90%"} height={"80%"} />
								</Box>
							)}
						</VideoWrapper>
					)}
					{mainTab === "roomScan" && (
						<RoomScanPanel
							arrStamps={roomScanControl.timestampsArr}
							controls={roomScanControl.options}
							dispatch={roomScanControl.dispatch}
							displayStateAtom={roomscanDisplayStateAtom}
							isDeleted={selectedProctoringSession?.isRoomScanDeleted}
							deleteCause={selectedProctoringSession.roomScanDeleteCause}
							isFullscreen={inFullscreen}
							onPlayVideo={roomScanControl.play}
							options={VIDEO_JS_OPTIONS}
							setVideoDuration={roomScanControl.changeDuration}
							videoTimer={roomScanControl.playback}
							sources={roomScanControl.sources}
							startDate={startDate}
							toggledTimestamps={showTimestamps}
							videoHasError={roomScanControl.hasError}
							setVideoHasError={roomScanControl.toggleError}
							notes={roomScanNotesControl.notes}
							hasFinishedPlaying={roomScanControl.hasFinishedPlaying}
							isOverlayShowing={roomScanControl.isOverlayShowing}
							hasInstituteEditPermission={proctoringState.hasInstituteEditPermission}
							sessionStatus={sessionStatus}
							maxDuration={roomScanControl.duration}
							volumeProps={{
								showVolume: !selectedProctoringSession.isRoomScanDeleted,
								onVolumeButtonClick: roomScanControl.toggleVolume,
								onVolumeInputChange: (e) => roomScanControl.setVolume(Number(e.target.value)),
							}}
							onVideoSpeedChange={roomScanControl.setSpeed}
							onDeleteRecording={() => componentsControl.modal.toggleDeleteConfirmationModal("roomscan")}
							onFullScreenClick={fullScreenControl.setFullscreen}
						/>
					)}
					{mainTab === "idPhotos" && (
						<IdPhotosPanel
							idPhotoSrc={getIdPhotosSrc(sessionUuid, "id")}
							personPhotoSrc={getIdPhotosSrc(sessionUuid, "person")}
							handleImgError={componentsControl.image.handleImageError}
						/>
					)}
					{mainTab === "signature" && (
						<SignatureTabPanel
							imageURL={session.signatureUrl.value}
							signatureType={selectedProctoringSession?.signatureType}
						/>
					)}
					{mainTab === "scratchNote" && <ScratchNote images={session.scratchNoteLinks.value} />}
					{mainTab === "config" && (
						<ConfigurationPanel
							proctoringAssessmentConfiguration={proctoringState.proctoringOptions}
							proctoringData={proctoringState.proctoringConfigData}
							verityInstituteSettings={proctoringState.verityInstituteSettings}
							optionRelatedInstituteSettings={proctoringState.optionRelatedInstituteSettings}
						/>
					)}
				</Grid>
			</Grid>
			<Grid
				item
				md={12}
				lg={5}
				sx={{
					borderLeft: { lg: "0.5px solid #CACACA", md: "none" }, // Apply border-left at md and above
					borderTop: { xs: "0.5px solid #CACACA", md: "0.5px solid #CACACA", lg: "none" }, // Apply border-top at xs and below
				}}
				container
				flexDirection={"column"}
			>
				<StudentSelectorLayout
					buttonType="text"
					onBack={componentsControl.selectStudent.handleSelectPreviousStudent}
					onNext={componentsControl.selectStudent.handleSelectNextStudent}
					onStudentChange={componentsControl.selectStudent.handleSelectStudent}
					studentList={viewProps.studentList}
					selectedStudent={viewProps.selectedStudent}
				/>
				<Grid item container paddingX={"20px"} paddingTop="14px" gap="14px" display={"flex"} flexDirection={"column"}>
					<Grid item container alignItems="center" gap={"6px"} minHeight="30px">
						<Typography variant="h2" fontWeight={500} fontSize="15px">
							{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.attemptStatus")}
						</Typography>
						<SessionStatusChip status={sessionStatus} />
					</Grid>
					<AttemptSelector
						sessionNumber={sessionNumber}
						onChange={session.updateSessionIndex}
						proctoringSessions={viewProps.proctoringSessions}
					/>
					{mainTab !== "roomScan" && (
						<>
							{tabControl.secondaryTabList.length > 1 && (
								<CoreTabs
									tabs={tabControl.secondaryTabList}
									width="100%"
									tabProps={{ sx: { width: `max(calc(100%/${tabControl.secondaryTabList.length}), 70px)` } }}
									height={"48px"}
									value={secondaryTab}
									onChange={(_, newTab) => {
										tabControl.handleSecondaryTabChange(newTab);
										if (newTab === "notes") {
											void sessionNotesControl.refresh();
										}
									}}
								/>
							)}
							<div
								style={{ width: "100%" }}
								role={tabControl.secondaryTabList.length > 1 ? "tabpanel" : undefined}
								aria-labelledby={
									tabControl.secondaryTabList.length > 1 ? tabControl.secondaryTabIdRecord[secondaryTab] : undefined
								}
								key={secondaryTab}
							>
								{secondaryTab === "flaggedEvents" &&
									(summaryControl.showing ? (
										<SummaryPanel
											getDefaultSummary={summaryControl.getDefault}
											onClose={summaryControl.hide}
											onSave={summaryControl.save}
											isLiveSessionProctor={proctoringState.isLiveSessionProctor}
											sessionSummary={summaryControl.summary}
										/>
									) : (
										<TimestampLinks
											sessionId={sessionUuid}
											events={events}
											dispatch={webcamControl.dispatch}
											setPlay={webcamControl.play}
											debugger={"webcam"}
											options={VIDEO_JS_FRAME_OPTIONS}
											controls={webcamControl.options}
											arrStamps={webcamControl.timestampsArr}
											sources={webcamControl.sources[0] ?? {}}
											globaltime={webcamControl.playback.timer / 1000}
											skip={webcamControl.playback.skip}
											offsetGetter={timeOffset}
											isLoading={isLoading}
											mediaGridHeight={componentsControl.mediaGrid.mediaGridHeight}
											assessmentName={assessmentName}
											studentName={studentName}
											duration={webcamControl.duration ?? 0}
											updateEvents={eventsControl.update}
											courseName={proctoringState.courseName}
											attemptNumber={sessionNumber}
											assessmentStatus={selectedProctoringSession?.status ?? "Unknown"}
											recordingDate={eventsControl.firstEventDate?.toLocaleDateString()}
											startDate={startDate}
											hasTabs={tabControl.secondaryTabList.length > 1}
											summaryButton={
												<SummaryButton
													isLiveSessionProctor={proctoringState.isLiveSessionProctor}
													showAiSummary={summaryControl.showAi}
													showSummary={summaryControl.show}
													loading={summaryControl.loading}
													sessionSummary={summaryControl.summary}
												/>
											}
										/>
									))}
								{secondaryTab === "notes" && (
									<div role="tabpanel" className={styles.notesTabpanel}>
										<NotesPanel
											sessionNotes={sessionNotesControl.notes}
											dispatch={webcamControl.dispatch}
											duration={webcamControl.duration ?? 0}
											offsetGetter={timeOffset}
											onAddNote={sessionNotesControl.addNote}
											editNote={sessionNotesControl.editNote}
											deleteNote={sessionNotesControl.deleteNote}
											thumbnails={sessionNotesControl.thumbnail}
										/>
									</div>
								)}
								{secondaryTab === "liveNotes" && (
									<LiveNotesPanel
										liveNotes={notesControl.visibleLiveNotes}
										thumbnails={sessionNotesControl.thumbnail}
										dispatch={webcamControl.dispatch}
										duration={webcamControl.duration ?? 0}
										offsetGetter={timeOffset}
									/>
								)}
							</div>
						</>
					)}
					{mainTab === "roomScan" && (
						<RoomScanNotePanel
							dispatch={roomScanControl.dispatch}
							duration={roomScanControl.duration ?? 0}
							offsetGetter={0}
							onAddNote={roomScanNotesControl.addNote}
							editNote={roomScanNotesControl.editNote}
							deleteNote={roomScanNotesControl.deleteNote}
							sessionNotes={roomScanNotesControl.notes}
							thumbnails={roomScanNotesControl.thumbnail}
						/>
					)}
				</Grid>
			</Grid>
			{componentsControl.modal.isDeleteConfirmationModalShown !== undefined && (
				<DeleteConfirmationModal
					onClose={() => componentsControl.modal.toggleDeleteConfirmationModal(undefined)}
					onDelete={componentsControl.modal.deleteRecording}
				/>
			)}
		</Grid>
	);
}

type VerityAssessmentLayoutProps = ProctoringResultLayoutProps & {
	verityAssessment: VerityAssessment;
};

function VerityAssessmentLayout({ logic, viewProps, verityAssessment }: VerityAssessmentLayoutProps) {
	const verityAssessmentLogic = useVerityAssessmentResult({ verityAssessment, session: logic.session });

	const {
		assignmentControl,
		totalPoints,
		earnedPoints,
		gradePercent,
		quizQuestions,
		quizResponses,
		savingGrade,
		handleSaveGrade,
		modal,
	} = verityAssessmentLogic;

	const { showTimestamps } = viewProps;
	const {
		componentsControl,
		summaryControl,
		proctoringState,
		session,
		eventsControl,
		isLoading,
		notesControl,
		webcamControl,
		roomScanControl,
		timeOffset,
		tabControl,
		fullScreenControl,
		screenShareControl,
		proctoredMediaContainer,
	} = logic;

	const { assessmentName, studentName, isExemptedNoProctoring } = proctoringState;
	const { sessionNumber, selectedProctoringSession, sessionUuid, sessionStatus } = session;
	const { all: events, startDate } = eventsControl;

	const { roomScan: roomScanNotesControl, session: sessionNotesControl } = notesControl;

	const { inFullscreen } = fullScreenControl;
	const { mainTab, secondaryTab } = tabControl;

	const verityAssignmentDraft = getAssignmentDraft(verityAssessmentLogic.grade.drafts);

	return (
		<Grid container sx={{ background: "#F7F7F9", minHeight: "100%" }}>
			<Grid item container md={12} lg={7} gap={"0px"} flexDirection={"column"} paddingBottom={"14px"} height={"100%"}>
				<div className={styles.verityAssessmentTopCard}>
					<div className={styles.verityAssessmentHeader}>
						<div className={styles.assessmentTitleWrapper}>
							<ResultViewBackButton
								showBackButton={componentsControl.back.showBackButton}
								backLabel={assessmentName}
								onBackButtonClick={() =>
									verityAssessmentLogic.handleAction(() => componentsControl.back.handleBackButton())
								}
							/>
							<StudentSelectorLayout
								buttonType="icon"
								onBack={() =>
									verityAssessmentLogic.handleAction(() =>
										componentsControl.selectStudent.handleSelectPreviousStudent(),
									)
								}
								onNext={() =>
									verityAssessmentLogic.handleAction(() => componentsControl.selectStudent.handleSelectNextStudent())
								}
								onStudentChange={(studentId) =>
									verityAssessmentLogic.handleAction(() =>
										componentsControl.selectStudent.handleSelectStudent(studentId)(),
									)
								}
								studentList={viewProps.studentList}
								selectedStudent={viewProps.selectedStudent}
								removeBorderStyling={false}
								studentSelectorClassName={styles.studentSelectorVerity}
								className={styles.studentSelectorLayoutVerity}
							/>
						</div>
						<CoreSkeletonWrapper loading={verityAssessmentLogic.loading} width={200} height={"48px"}>
							<div className={styles.saveButtonsWrapper}>
								<CoreButton
									color="secondary"
									onClick={() => handleSaveGrade(false)}
									disabled={verityAssessmentLogic.saveDisabled}
									loading={savingGrade === "draft"}
								>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.saveLater")}
								</CoreButton>
								<CoreButton
									onClick={() => handleSaveGrade(true)}
									disabled={verityAssessmentLogic.saveDisabled}
									loading={savingGrade === "publish"}
								>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.saveGrade")}
								</CoreButton>
							</div>
						</CoreSkeletonWrapper>
					</div>
					<div className={styles.assessmentInfoContainer}>
						<div className={styles.attemptInfoWrapper}>
							<AttemptSelector
								sessionNumber={sessionNumber}
								onChange={(index) => verityAssessmentLogic.handleAction(() => session.updateSessionIndex(index))}
								proctoringSessions={viewProps.proctoringSessions}
								className={styles.verityAtttmptSelector}
							/>
							<SessionStatusChip status={sessionStatus} isLate={verityAssessmentLogic.isLateSubmission} />
							{verityAssessment.type === "quiz" && quizQuestions.length > 0 ? (
								<>
									<CoreChip chipColor="#F5E5DE" variant="rectangular" sx={{ minWidth: "unset" }}>
										{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questionsLabel", {
											defaultValue: "Questions:",
										})}{" "}
										{quizQuestions.length}
									</CoreChip>
									<CoreChip chipColor="#E4E3F6" variant="rectangular" sx={{ minWidth: "unset" }}>
										{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.scoreLabel", {
											defaultValue: "Score:",
										})}{" "}
										{earnedPoints} / {totalPoints}
									</CoreChip>
									<CoreChip chipColor="#F9E4F5" variant="rectangular" sx={{ minWidth: "unset" }}>
										{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.gradeLabel", {
											defaultValue: "Grade:",
										})}{" "}
										{gradePercent}%
									</CoreChip>
								</>
							) : (
								<>
									{assignmentControl.display.showFileCountChip && (
										<CoreChip chipColor="#E4E3F6" variant="rectangular">
											{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.submittedFiles", {
												count: assignmentControl.files.student.length,
											})}
										</CoreChip>
									)}
									<CoreChip chipColor="#F9E4F5" variant="rectangular">
										{verityAssessmentLogic.numAttemptsText}
									</CoreChip>
								</>
							)}
						</div>
						{assignmentControl.display.showInstructorFiles && (
							<div className={styles.verityAssignmentInstructionsContainer}>
								<Typography variant="h2" className={styles.title}>
									{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.assignmentInstructions")}
								</Typography>
								{assignmentControl.files.instructor.map((file, index) => (
									<CoreFileCard
										key={index}
										file={file}
										showDownload
										onRefetch={() => assignmentControl.fetch.instructor(file.name)}
									/>
								))}
							</div>
						)}
					</div>
				</div>
				{verityAssessmentLogic.banner.bannerType !== undefined && (
					<div className={styles.bannerContainer}>
						<CoreBanner label={verityAssessmentLogic.banner.text} color={verityAssessmentLogic.banner.bannerType} />
					</div>
				)}
				{verityAssessment.type === "quiz" && (
					<div className={styles.verityQuizContainer}>
						<QuizResultPanel
							quizQuestions={quizQuestions}
							quizResponses={quizResponses}
							grade={verityAssessmentLogic.grade}
						/>
					</div>
				)}
				{verityAssessment.type === "assignment" && (
					<CoreSkeletonWrapper loading={!verityAssignmentDraft} height={400}>
						<AssignmentPanel
							{...assignmentControl}
							grade={verityAssessmentLogic.grade}
							quizQuestions={verityAssessmentLogic.quizQuestions}
							draft={verityAssignmentDraft!}
							responseId={getAssignmentResponseId(verityAssessmentLogic.grade.drafts)}
						/>
					</CoreSkeletonWrapper>
				)}
			</Grid>
			<Grid
				item
				md={12}
				lg={5}
				sx={{
					background: "white",
					borderLeft: { lg: "0.5px solid #CACACA", md: "none" }, // Apply border-left at md and above
					borderTop: { xs: "0.5px solid #CACACA", md: "0.5px solid #CACACA", lg: "none" }, // Apply border-top at xs and below
				}}
				container
				flexDirection={"column"}
			>
				<div className={styles.verityTabsWrapper}>
					<CoreSkeletonWrapper loading={isLoading} width={"100%"} height={"48px"}>
						<CoreTabs
							tabs={tabControl.mainTabList}
							value={mainTab}
							secondary
							height={"36px"}
							onChange={(_, newTab) => {
								tabControl.handleMainTabChange(newTab);
							}}
							tabsProps={{
								sx: {
									maxWidth: "100%",
								},
								ref: tabControl.mainTabsRef,
							}}
							scrollable
							scrollButtons={tabControl.mainTabOverflow}
						/>
					</CoreSkeletonWrapper>
				</div>
				<div className={styles.mainTabPanelWrapper}>
					<Grid
						item
						sx={{ height: "auto", maxHeight: "45vh", width: "100%" }}
						id="media_grid"
						role="tabpanel"
						aria-labelledby={tabControl.mainTabIdRecord[mainTab]}
						key={mainTab}
					>
						{mainTab === "recording" && (
							<VideoWrapper inFullscreen={inFullscreen} showTimestamps={showTimestamps}>
								{logic.areVideosFetched ? (
									<ProctoringVideoPlayer
										isFullscreen={inFullscreen}
										sessionId={sessionUuid}
										showTimestamps={showTimestamps}
										controlOptions={webcamControl.options}
										playback={webcamControl.playback}
										playbackDispatch={webcamControl.dispatch}
										sessionRecordingTimestamps={eventsControl.sessionRecordingTimestamps}
										videos={{ webcam: webcamControl.sources, screencapture: webcamControl.screenSources }}
										startDate={startDate}
										duration={webcamControl.duration}
										timeOffset={timeOffset}
										setFullscreen={fullScreenControl.setFullscreen}
										setPlay={webcamControl.play}
										setVolume={(e) => webcamControl.setVolume(Number(e.target.value))}
										setSpeed={webcamControl.setSpeed}
										onEmailModalChange={componentsControl.modal.toggleEmailModal}
										shouldShowDownload={webcamControl.shouldShowDownload}
										showVideoUnavailable={webcamControl.showVideoUnavailable}
										showAudioThumbnail={webcamControl.showAudioThumbnail}
										webcamVideoOptions={VIDEO_JS_OPTIONS}
										screencaptureVideoOptions={VIDEO_JS_OPTIONS}
										shouldHideCamVid={webcamControl.shouldHideCamVid}
										events={events}
										sessionNotes={sessionNotesControl.notes}
										visibleLiveNotes={notesControl.visibleLiveNotes}
										toggleVolume={webcamControl.toggleVolume}
										isWebcamAndScreencaptureDeleted={selectedProctoringSession?.isWebcamAndScreenCaptureDeleted}
										webcamAndScreencaptureDeleteCause={selectedProctoringSession?.webcamAndScreenCaptureDeleteCause}
										setIsDeleteConfirmationModalShown={componentsControl.modal.toggleDeleteConfirmationModal}
										hasInstituteEditPermission={proctoringState.hasInstituteEditPermission}
										sessionStatus={sessionStatus}
										webCamHasError={webcamControl.hasError}
										setWebCamHasError={webcamControl.toggleError}
										selectedProctoringSessionUuid={sessionUuid}
										showMachineLearningOverlay={webcamControl.showMachineLearningOverlay}
										screenCaptureHasError={screenShareControl.hasError}
										setScreenCaptureHasError={screenShareControl.toggleError}
										proctoredMediaContainer={proctoredMediaContainer}
									/>
								) : (
									<Box width={"100%"} height={"100%"} display={"flex"} alignItems={"center"} justifyContent={"center"}>
										<Skeleton variant="rectangular" width={"90%"} height={"80%"} />
									</Box>
								)}
							</VideoWrapper>
						)}
						{mainTab === "roomScan" && (
							<RoomScanPanel
								arrStamps={roomScanControl.timestampsArr}
								controls={roomScanControl.options}
								dispatch={roomScanControl.dispatch}
								displayStateAtom={roomscanDisplayStateAtom}
								isDeleted={selectedProctoringSession?.isRoomScanDeleted}
								deleteCause={selectedProctoringSession.roomScanDeleteCause}
								isFullscreen={inFullscreen}
								onPlayVideo={roomScanControl.play}
								options={VIDEO_JS_OPTIONS}
								setVideoDuration={roomScanControl.changeDuration}
								videoTimer={roomScanControl.playback}
								sources={roomScanControl.sources}
								startDate={startDate}
								toggledTimestamps={showTimestamps}
								videoHasError={roomScanControl.hasError}
								setVideoHasError={roomScanControl.toggleError}
								notes={roomScanNotesControl.notes}
								hasFinishedPlaying={roomScanControl.hasFinishedPlaying}
								isOverlayShowing={roomScanControl.isOverlayShowing}
								hasInstituteEditPermission={proctoringState.hasInstituteEditPermission}
								sessionStatus={sessionStatus}
								maxDuration={roomScanControl.duration}
								volumeProps={{
									showVolume: !selectedProctoringSession.isRoomScanDeleted,
									onVolumeButtonClick: roomScanControl.toggleVolume,
									onVolumeInputChange: (e) => roomScanControl.setVolume(Number(e.target.value)),
								}}
								onVideoSpeedChange={roomScanControl.setSpeed}
								onDeleteRecording={() => componentsControl.modal.toggleDeleteConfirmationModal("roomscan")}
								onFullScreenClick={fullScreenControl.setFullscreen}
							/>
						)}
						{mainTab === "idPhotos" && (
							<IdPhotosPanel
								idPhotoSrc={getIdPhotosSrc(sessionUuid, "id")}
								personPhotoSrc={getIdPhotosSrc(sessionUuid, "person")}
								handleImgError={componentsControl.image.handleImageError}
							/>
						)}
						{mainTab === "signature" && (
							<SignatureTabPanel
								imageURL={session.signatureUrl.value}
								signatureType={selectedProctoringSession?.signatureType}
							/>
						)}
						{mainTab === "scratchNote" && <ScratchNote images={session.scratchNoteLinks.value} />}
						{mainTab === "config" && (
							<ConfigurationPanel
								proctoringAssessmentConfiguration={proctoringState.proctoringOptions}
								proctoringData={proctoringState.proctoringConfigData}
								verityInstituteSettings={proctoringState.verityInstituteSettings}
								optionRelatedInstituteSettings={proctoringState.optionRelatedInstituteSettings}
							/>
						)}
					</Grid>
					{!isExemptedNoProctoring && (
						<FlagLayout
							type="square"
							severeCount={eventsControl.severeCount}
							majorCount={eventsControl.majorCount}
							minorCount={eventsControl.minorCount}
						/>
					)}
				</div>
				<Grid item container paddingX={"20px"} paddingTop="14px" gap="14px" display={"flex"} flexDirection={"column"}>
					{mainTab !== "roomScan" && (
						<>
							{tabControl.secondaryTabList.length > 1 && (
								<CoreTabs
									tabs={tabControl.secondaryTabList}
									width="100%"
									tabProps={{ sx: { width: `max(calc(100%/${tabControl.secondaryTabList.length}), 70px)` } }}
									height={"48px"}
									value={secondaryTab}
									onChange={(_, newTab) => {
										tabControl.handleSecondaryTabChange(newTab);
										if (newTab === "notes") {
											void sessionNotesControl.refresh();
										}
									}}
								/>
							)}
							<div
								style={{ width: "100%" }}
								role={tabControl.secondaryTabList.length > 1 ? "tabpanel" : undefined}
								aria-labelledby={
									tabControl.secondaryTabList.length > 1 ? tabControl.secondaryTabIdRecord[secondaryTab] : undefined
								}
								key={secondaryTab}
							>
								{secondaryTab === "flaggedEvents" &&
									(summaryControl.showing ? (
										<SummaryPanel
											getDefaultSummary={summaryControl.getDefault}
											onClose={summaryControl.hide}
											onSave={summaryControl.save}
											isLiveSessionProctor={proctoringState.isLiveSessionProctor}
											sessionSummary={summaryControl.summary}
										/>
									) : (
										<TimestampLinks
											sessionId={sessionUuid}
											events={events}
											dispatch={webcamControl.dispatch}
											setPlay={webcamControl.play}
											debugger={"webcam"}
											options={VIDEO_JS_FRAME_OPTIONS}
											controls={webcamControl.options}
											arrStamps={webcamControl.timestampsArr}
											sources={webcamControl.sources[0] ?? {}}
											globaltime={webcamControl.playback.timer / 1000}
											skip={webcamControl.playback.skip}
											offsetGetter={timeOffset}
											isLoading={isLoading}
											mediaGridHeight={componentsControl.mediaGrid.mediaGridHeight}
											assessmentName={assessmentName}
											studentName={studentName}
											duration={webcamControl.duration ?? 0}
											updateEvents={eventsControl.update}
											courseName={proctoringState.courseName}
											attemptNumber={sessionNumber}
											assessmentStatus={selectedProctoringSession?.status ?? "Unknown"}
											recordingDate={eventsControl.firstEventDate?.toLocaleDateString()}
											startDate={startDate}
											hasTabs={tabControl.secondaryTabList.length > 1}
											summaryButton={
												<SummaryButton
													isLiveSessionProctor={proctoringState.isLiveSessionProctor}
													showAiSummary={summaryControl.showAi}
													showSummary={summaryControl.show}
													loading={summaryControl.loading}
													sessionSummary={summaryControl.summary}
												/>
											}
										/>
									))}
								{secondaryTab === "notes" && (
									<div role="tabpanel" className={styles.notesTabpanel}>
										<NotesPanel
											sessionNotes={sessionNotesControl.notes}
											dispatch={webcamControl.dispatch}
											duration={webcamControl.duration ?? 0}
											offsetGetter={timeOffset}
											onAddNote={sessionNotesControl.addNote}
											editNote={sessionNotesControl.editNote}
											deleteNote={sessionNotesControl.deleteNote}
											thumbnails={sessionNotesControl.thumbnail}
										/>
									</div>
								)}
								{secondaryTab === "liveNotes" && (
									<LiveNotesPanel
										liveNotes={notesControl.visibleLiveNotes}
										thumbnails={sessionNotesControl.thumbnail}
										dispatch={webcamControl.dispatch}
										duration={webcamControl.duration ?? 0}
										offsetGetter={timeOffset}
									/>
								)}
							</div>
						</>
					)}
					{mainTab === "roomScan" && (
						<RoomScanNotePanel
							dispatch={roomScanControl.dispatch}
							duration={roomScanControl.duration ?? 0}
							offsetGetter={0}
							onAddNote={roomScanNotesControl.addNote}
							editNote={roomScanNotesControl.editNote}
							deleteNote={roomScanNotesControl.deleteNote}
							sessionNotes={roomScanNotesControl.notes}
							thumbnails={roomScanNotesControl.thumbnail}
						/>
					)}
				</Grid>
			</Grid>
			{componentsControl.modal.isDeleteConfirmationModalShown !== undefined && (
				<DeleteConfirmationModal
					onClose={() => componentsControl.modal.toggleDeleteConfirmationModal(undefined)}
					onDelete={componentsControl.modal.deleteRecording}
				/>
			)}
			{modal.open && (
				<UnsavedChangesModal
					onCancel={modal.discard}
					onSaveLater={() => modal.save(false)}
					onSaveGrade={() => modal.save(true)}
					onClose={modal.close}
					attemptNumber={sessionNumber}
				/>
			)}
		</Grid>
	);
}

export type ProctoringResultViewProps = {
	// sessionUuid: string;
	instituteId: number;
	courseId: number;
	// sessionNum: string | undefined | null;
	showVideo: boolean;
	/**
	 * defaults to false
	 */
	showTimestamps?: boolean;
	selectedLiveNotes?: LiveSessionNote[];
	// selectedProctoringSession?: SessionInfo | null;
	// totalSessions?: number | null;
	proctoringSessions: SessionInfo[];
	liveSessions: Record<string, LiveSessionInfo>;
	setProctoringSessions?: Dispatch<SetStateAction<SessionInfo[] | undefined>>;
	setShowStudentResults?: (value: boolean) => void;
	assessmentInfo: QuizInfoSummary | null;
	selectedStudent?: number;
	setSelectedStudent: (value: number | undefined) => void;
	studentList: { id: number; name: string; severityLevel?: number }[];
	initialProctoringSessionId?: string;
	exemptedStudents?: Omit<ExemptedStudent, "name">[];
};

export const ProctoringResultView = (props: ProctoringResultViewProps) => {
	const viewProps = { ...props, showTimestamps: props.showTimestamps ?? false };
	const logic = useProctoringResultView(viewProps);

	if (logic.proctoringState.verityAssessment) {
		return (
			<VerityAssessmentLayout
				viewProps={viewProps}
				logic={logic}
				verityAssessment={logic.proctoringState.verityAssessment}
			/>
		);
	}

	return <LmsAssessmentLayout viewProps={viewProps} logic={logic} />;
};

export default ProctoringResultView;
